import React, { useEffect, useState } from "react";

import api, { IMAGE_BASE_URL } from "../api/Api.js";

import printImage from "../assets/images/print.png";
import newReservationImage from "../assets/images/newreservation.png";
import tickImage from "../assets/images/tickm.png";

import { useNavigate, useLocation } from "react-router-dom";

import "bootstrap/dist/css/bootstrap.min.css";
import "../assets/css/style.css";

import { useLanguage } from "../context/LanguageContext";

function ConfirmBooking() {
  const navigate = useNavigate();
  const location = useLocation();

  // ==========================================================
  // LANGUAGE
  // ==========================================================

  const { language, t } = useLanguage();

  const languageId = Number(
    language?.lang_id || language?.language_id || language?.id || language || 1
  );

  // ==========================================================
  // TRANSLATION HELPERS
  // ==========================================================

  const bt = (key, fallback = "") => t(`bookingengine.${key}`, fallback);

  const pt = (key, fallback = "") => t(`personaldtls.${key}`, fallback);

  const rt = (key, fallback = "") => t(`ratechart.${key}`, fallback);

  const et = (key, fallback = "") => t(`extracharges.${key}`, fallback);

  const ot = (key, fallback = "") => t(`others.${key}`, fallback);

  // ==========================================================
  // STATE
  // ==========================================================

  const [booking, setBooking] = useState(null);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  const [locations, setLocations] = useState([]);

  const [locationsLoading, setLocationsLoading] = useState(false);

  const [freeExtras, setFreeExtras] = useState([]);

  const [paidExtras, setPaidExtras] = useState([]);

  const [freeInsurance, setFreeInsurance] = useState([]);

  const [paidInsurance, setPaidInsurance] = useState([]);

  const [extrasLoading, setExtrasLoading] = useState(false);

  // ==========================================================
  // EMAIL STATE
  // ==========================================================

  const [mailStatus, setMailStatus] = useState("idle");

  const [mailMessage, setMailMessage] = useState("");

  // ==========================================================
  // FEATURES
  // ==========================================================

  const [featuresExpanded, setFeaturesExpanded] = useState(false);

  // ==========================================================
  // TRANSLATED CAR
  // (name, category, specs and features in the selected language)
  // ==========================================================

  const [translatedCar, setTranslatedCar] = useState(null);

  // Depend on primitive values only, not the whole booking object
  const bookingCarId = booking?.car?.carmodel_id;
  const bookingCarName = booking?.car?.carmodel_name;

  useEffect(() => {
    if (!bookingCarId && !bookingCarName) return;

    let cancelled = false;

    const loadCarName = async () => {
      try {
        const response = await api.get("/cars/getCars", {
          params: { lang_id: languageId },
        });

        if (cancelled) return;

        const list =
          response.data?.success && Array.isArray(response.data.data)
            ? response.data.data
            : [];

        // 1. Match by ID (preferred)
        let found = bookingCarId
          ? list.find(
              (item) =>
                String(item.carmodel_id ?? item.car_id ?? item.id) ===
                String(bookingCarId)
            )
          : null;

        // 2. Fallback: match by the name stored in the booking
        if (!found && bookingCarName) {
          found = list.find(
            (item) =>
              String(item.carmodel_name ?? item.name ?? "")
                .trim()
                .toLowerCase() === String(bookingCarName).trim().toLowerCase()
          );
        }

        console.log(
          "CAR NAME LANGUAGE:",
          languageId,
          "CAR ID:",
          bookingCarId,
          "FOUND:",
          found
        );

        setTranslatedCar(found || null);
      } catch (err) {
        console.error("Car translation error:", err);

        if (!cancelled) setTranslatedCar(null);
      }
    };

    loadCarName();

    return () => {
      cancelled = true;
    };
  }, [bookingCarId, bookingCarName, languageId]);

  // ==========================================================
  // BOOKING ID
  // ==========================================================

  const resolveBookingId = () => {
    // 1. React navigation state
    if (location.state?.bookingid) {
      return location.state.bookingid;
    }

    // 2. sessionStorage
    try {
      const stored = sessionStorage.getItem("finalBooking");

      if (stored) {
        const parsed = JSON.parse(stored);

        if (parsed?.bookingid) {
          return parsed.bookingid;
        }
      }
    } catch (error) {
      console.warn("Unable to read finalBooking:", error);
    }

    // 3. URL
    const params = new URLSearchParams(location.search);

    return params.get("bookingid") || null;
  };

  // ==========================================================
  // LOAD LOCATIONS
  // ==========================================================

  useEffect(() => {
    const loadLocations = async () => {
      try {
        setLocationsLoading(true);

        const response = await api.get("/locations/getLocations", {
          params: {
            city_id: 1,
            lang_id: languageId,
          },
        });

        const result = response.data;

        if (result?.success && Array.isArray(result.data)) {
          setLocations(result.data);
        } else {
          setLocations([]);
        }
      } catch (error) {
        console.error("Location loading error:", error);

        setLocations([]);
      } finally {
        setLocationsLoading(false);
      }
    };

    loadLocations();
  }, [languageId]);

  // ==========================================================
  // LOAD EXTRAS
  // ==========================================================

  useEffect(() => {
    const loadExtras = async () => {
      try {
        setExtrasLoading(true);

        const response = await api.get("/extras", {
          params: {
            lang_id: languageId,
          },
        });

        const result = response.data;

        if (!result?.success) {
          setFreeExtras([]);
          setPaidExtras([]);
          setFreeInsurance([]);
          setPaidInsurance([]);

          return;
        }

        setFreeExtras(
          Array.isArray(result.freeExtras) ? result.freeExtras : []
        );

        setPaidExtras(
          Array.isArray(result.paidExtras) ? result.paidExtras : []
        );

        setFreeInsurance(
          Array.isArray(result.freeInsurance) ? result.freeInsurance : []
        );

        setPaidInsurance(
          Array.isArray(result.paidInsurance) ? result.paidInsurance : []
        );
      } catch (error) {
        console.error("Extras loading error:", error);

        setFreeExtras([]);
        setPaidExtras([]);
        setFreeInsurance([]);
        setPaidInsurance([]);
      } finally {
        setExtrasLoading(false);
      }
    };

    loadExtras();
  }, [languageId]);

  // ==========================================================
  // GET LOCATION NAME
  // ==========================================================

  const getLocationName = (id) => {
    if (id === undefined || id === null || id === "") {
      return bt("notSelected", "Not selected");
    }

    const found = locations.find((item) => Number(item.loc_id) === Number(id));

    if (found) {
      return found.loc_name;
    }

    return String(id);
  };

  // ==========================================================
  // TIME
  // ==========================================================

  const getTime = (hour, minute) => {
    if (hour === undefined || hour === null || hour === "") {
      return "";
    }

    return `${hour}:${String(minute ?? "00").padStart(2, "0")}`;
  };

  // ==========================================================
  // MAP DATABASE BOOKING
  // ==========================================================

  const mapDbBookingToViewModel = (row) => {
    let sessionExtras = {
      freeExtras: [],
      paidExtras: {},
      freeInsurance: [],
      paidInsurance: {},
    };

    let sessionPaymentMethod = null;

    // Coupon fallback taken from the booking page (same browser session)
    let sessionCoupon = {
      code: "",
      perc: 0,
      amt: 0,
    };

    try {
      const stored = sessionStorage.getItem("finalBooking");

      if (stored) {
        const parsed = JSON.parse(stored);

        if (String(parsed?.bookingid) === String(row?.bookingid)) {
          sessionExtras = {
            freeExtras: parsed?.bookingData?.freeExtras || [],

            paidExtras: parsed?.bookingData?.paidExtras || {},

            freeInsurance: parsed?.bookingData?.freeInsurance || [],

            paidInsurance: parsed?.bookingData?.paidInsurance || {},
          };

          sessionPaymentMethod = parsed?.paymentMethod || null;

          sessionCoupon = {
            code: parsed?.couponCode || "",
            perc: Number(parsed?.couponDiscountPercentage || 0),
            amt: Number(parsed?.couponDiscount || 0),
          };
        }
      }
    } catch (error) {
      console.warn("Unable to read finalBooking:", error);
    }

    const daysFromDates = () => {
      const pick = new Date(row?.online_pickdate);
      const drop = new Date(row?.online_returndate);

      if (Number.isNaN(pick.getTime()) || Number.isNaN(drop.getTime())) {
        return 1;
      }

      return Math.max(1, Math.ceil((drop - pick) / (1000 * 60 * 60 * 24)));
    };

    const rentalDays =
      Number(row?.online_diffdate) > 0
        ? Number(row.online_diffdate)
        : daysFromDates();

    const perDayCarPrice = Number(row?.online_carprice || 0);

    const rentalPrice = perDayCarPrice * rentalDays;

    const pickupCharge = Number(row?.online_locpickupcharge || 0);

    const dropoffCharge = Number(row?.online_locdropoffcharge || 0);

    const oneWayCharge = Number(row?.online_onewayrentalcharges || 0);

    // ------------------------------------------------------
    // COUPON (database first, session as fallback)
    // ------------------------------------------------------

    const couponCode =
      String(row?.Coupon_Code ?? "").trim() || sessionCoupon.code || "";

    const couponPerc =
      Number(row?.Coup_DiscountPerc || 0) || Number(sessionCoupon.perc || 0);

    const couponAmt =
      Number(row?.Coup_DiscountAmt || 0) || Number(sessionCoupon.amt || 0);

    return {
      bookingid: row?.bookingid,
      fullbooking_no: row?.fullbooking_no,

      car: {
        // IMPORTANT: needed to load the translated car.
        // Check the "BOOKING DETAILS ROW" console log for the real
        // field name and put it first in this list.
        carmodel_id:
          row?.online_carmodelid ??
          row?.carmodel_id ??
          row?.car_id ??
          row?.carmodelid ??
          row?.online_carmodel_id ??
          row?.online_carid,

        carmodel_name: row?.carmodel_name,

        carmodel_image: row?.carmodel_image,

        cat_name: row?.cat_name,

        carmodel_passgrno: row?.carmodel_passgrno,

        carmodel_luggno: row?.carmodel_luggno,

        carmodel_doors: row?.carmodel_doors,

        transmission: row?.transmission || row?.gear,

        gear: row?.gear,

        airco: row?.airco,

        air_condition: row?.air_condition,

        pricePerDay: perDayCarPrice,
      },

      customer: {
        title: row?.online_title,

        firstName: row?.online_firstname,

        lastName: row?.online_lastname,

        email: row?.online_emailid || row?.online_email,

        phone: row?.online_mobileno || row?.online_telephone,

        mobile: row?.online_mobileno,

        address: row?.online_address,

        city: row?.online_city,

        country: row?.online_country,

        postcode: row?.online_postcode,

        remarks: row?.online_remarks || "",

        dropoffRemarks:
          row?.online_dropremarks || row?.online_dropoffremarks || "",

        DropDetails: row?.DropDetails,
        regualclientid: row?.online_clientid,
        PickDetails: row?.PickDetails,
      },

      bookingCriteria: {
        cmbPickLocation: row?.online_pickstation,

        cmbDropLocation: row?.online_returnstation,

        pickdate: row?.online_pickdate,

        returndate: row?.online_returndate,

        cmbpickHour: row?.online_picktime
          ? String(row.online_picktime).split(":")[0]
          : "",

        cmbpickmin: row?.online_picktime
          ? String(row.online_picktime).split(":")[1]
          : "",

        cmbreturnHour: row?.online_returntime
          ? String(row.online_returntime).split(":")[0]
          : "",

        cmbreturnmin: row?.online_returntime
          ? String(row.online_returntime).split(":")[1]
          : "",
      },

      rentalDays,

      rentalPrice,

      pickupCharge,

      dropoffCharge,

      oneWayCharge,

      rentalTotal: rentalPrice + pickupCharge + dropoffCharge + oneWayCharge,

      paidExtrasTotal: Number(row?.online_totaloptextras || 0),

      paidInsuranceTotal: Number(row?.online_totalinsurance || 0),

      finalTotal: Number(row?.online_onlineprice || 0),

      // COUPON
      couponCode,

      couponPerc,

      couponAmt,

      freeExtras: sessionExtras.freeExtras,

      paidExtras: sessionExtras.paidExtras,

      freeInsurance: sessionExtras.freeInsurance,

      paidInsurance: sessionExtras.paidInsurance,

      paymentMethod: sessionPaymentMethod || "paylater",
    };
  };

  // ==========================================================
  // SEND CONFIRMATION MAIL
  // ==========================================================

  const sendConfirmationMail = async (bookingid) => {
    try {
      setMailStatus("sending");

      setMailMessage(
        bt("sendingConfirmationEmail", "Sending booking confirmation email...")
      );

      const response = await api.post("/bookings/send-confirmation-mail", {
        bookingid,
      });

      const result = response.data;

      if (!result?.success) {
        throw new Error(result?.message || "Unable to send email.");
      }

      if (result.alreadySent) {
        setMailStatus("alreadySent");

        setMailMessage(
          bt(
            "confirmationEmailAlreadySent",
            "Booking confirmation email has already been sent."
          )
        );
      } else {
        setMailStatus("sent");

        setMailMessage(
          bt(
            "confirmationEmailSent",
            "Booking confirmation email has been sent successfully."
          )
        );
      }
    } catch (error) {
      console.error("Confirmation mail error:", error);

      setMailStatus("failed");

      setMailMessage(
        error?.response?.data?.message ||
          error?.message ||
          bt(
            "bookingConfirmedEmailFailed",
            "Booking is confirmed, but email could not be sent."
          )
      );
    }
  };

  // ==========================================================
  // FETCH BOOKING
  // ==========================================================

  useEffect(() => {
    const bookingid = resolveBookingId();

    if (!bookingid) {
      setError(bt("bookingReferenceNotFound", "Booking reference not found."));

      setLoading(false);

      return;
    }

    const fetchBooking = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await api.post("/bookings/details", {
          bookingid,
        });

        const result = response.data;

        if (!result || !result.success) {
          setError(
            result?.message ||
              bt(
                "unableToLoadBookingConfirmation",
                "Unable to load booking confirmation."
              )
          );

          return;
        }

        console.log("BOOKING DETAILS ROW:", result.data);

        const mappedBooking = mapDbBookingToViewModel(result.data);

        setBooking(mappedBooking);

        await sendConfirmationMail(bookingid);
      } catch (error) {
        console.error("Confirmation error:", error);

        setError(
          error?.response?.data?.message ||
            bt(
              "unableToLoadBookingConfirmation",
              "Unable to load booking confirmation."
            )
        );
      } finally {
        setLoading(false);
      }
    };

    fetchBooking();
  }, [navigate, location.state, location.search]);

  // ==========================================================
  // CAR IMAGE
  // ==========================================================

  const getCarImage = (image) => {
    if (!image) {
      return "";
    }

    let imagePath = String(image).trim();

    if (!imagePath) {
      return "";
    }

    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      return imagePath;
    }

    imagePath = imagePath.replace(/^\/+/, "");

    const imageBase = IMAGE_BASE_URL || "";

    if (imagePath.startsWith("images/")) {
      return `${imageBase}/${imagePath}`;
    }

    if (imagePath.startsWith("CarImage/")) {
      return `${imageBase}/images/${imagePath}`;
    }

    return `${imageBase}/images/CarImage/${imagePath}`;
  };

  // ==========================================================
  // CAR FEATURES (from the API, in the selected language)
  // ==========================================================

  const getFeatureValue = (item) => {
    if (item === undefined || item === null) {
      return "";
    }

    if (typeof item === "string" || typeof item === "number") {
      return String(item).trim();
    }

    if (typeof item === "object") {
      return String(
        item.item_name ||
          item.name ||
          item.mldynsec_val ||
          item.value ||
          item.title ||
          item.label ||
          item.description ||
          ""
      ).trim();
    }

    return "";
  };

  const getFeatures = (source) => {
    const list =
      (Array.isArray(source?.includedItems) && source.includedItems.length > 0
        ? source.includedItems
        : null) ||
      (Array.isArray(source?.features) && source.features.length > 0
        ? source.features
        : null) ||
      (Array.isArray(source?.included_items) && source.included_items.length > 0
        ? source.included_items
        : null) ||
      [];

    return list.map(getFeatureValue).filter(Boolean);
  };

  // ==========================================================
  // SELECTED EXTRA CHECK
  // ==========================================================

  const isSelectedId = (collection, id) => {
    if (Array.isArray(collection)) {
      return collection.some((item) => Number(item) === Number(id));
    }

    if (collection && typeof collection === "object") {
      return Number(collection[id] || 0) > 0;
    }

    return false;
  };

  // ==========================================================
  // LOADING
  // ==========================================================

  if (loading) {
    return (
      <div
        className="text-center"
        style={{
          padding: "100px 20px",
        }}
      >
        <h4>
          {bt("loadingBookingConfirmation", "Loading booking confirmation...")}
        </h4>
      </div>
    );
  }

  // ==========================================================
  // ERROR
  // ==========================================================

  if (error) {
    return (
      <div
        className="text-center"
        style={{
          padding: "100px 20px",
        }}
      >
        <h4>{error}</h4>

        <button
          type="button"
          className="nextbutton1 nextbutton"
          style={{
            border: "none",
            cursor: "pointer",
            marginTop: "20px",
          }}
          onClick={() => navigate("/bookacar")}
        >
          {bt("startNewBooking", ot("others6", "New reservation"))}
        </button>
      </div>
    );
  }

  if (!booking) {
    return null;
  }

  // ==========================================================
  // VALUES
  // ==========================================================

  const car = booking.car || {};

  const customer = booking.customer || {};

  const criteria = booking.bookingCriteria || {};

  const rentalDays = Number(booking.rentalDays || 1);

  const rentalPrice = Number(booking.rentalPrice || 0);

  const perdaycarprice = Number(car.pricePerDay || 0);

  const pickupCharge = Number(booking.pickupCharge || 0);

  const dropoffCharge = Number(booking.dropoffCharge || 0);

  const oneWayCharge = Number(booking.oneWayCharge || 0);

  const rentalTotal = Number(booking.rentalTotal || 0);

  const paidExtrasTotal = Number(booking.paidExtrasTotal || 0);

  const paidInsuranceTotal = Number(booking.paidInsuranceTotal || 0);

  const finalTotal = Number(booking.finalTotal || 0);

  // COUPON
  const couponCode = booking.couponCode || "";

  const couponPerc = Number(booking.couponPerc || 0);

  const couponAmt = Number(booking.couponAmt || 0);

  const fullBookingNo = booking.fullbooking_no || booking.bookingid;

  // ==========================================================
  // CAR DETAILS (translated car first, saved booking as fallback)
  // ==========================================================

  const tc = translatedCar || {};

  const carName =
    tc.name ||
    tc.car_name ||
    tc.carmodel_name ||
    car.carmodel_name ||
    bt("selectedCar", "Selected car");

  const category =
    tc.category || tc.car_category || tc.cat_name || car.cat_name || "";

  const carImage = getCarImage(
    tc.image || tc.carmodel_image || car.carmodel_image
  );

  const passengers =
    tc.passengers ?? tc.carmodel_passgrno ?? car.carmodel_passgrno ?? 0;

  const luggage = tc.luggage ?? tc.carmodel_luggno ?? car.carmodel_luggno ?? 0;

  const doors = tc.doors ?? tc.carmodel_doors ?? car.carmodel_doors ?? "-";

  const airValue = String(
    tc.carmodel_airconditionr ??
      tc.airConditioning ??
      tc.airco ??
      car.airco ??
      car.air_condition ??
      ""
  )
    .trim()
    .toLowerCase();

  const hasAirCo = !["", "0", "no", "false", "n", "none"].includes(airValue);

  // ==========================================================
  // FEATURES (dynamic, from the API in the selected language)
  // ==========================================================

  const features = getFeatures(tc);

  const mainFeatures = features.slice(0, 4);

  const extraFeatures = features.slice(4);

  // ==========================================================
  // EXTRAS
  // ==========================================================

  const paidExtrasSelection = booking.paidExtras || {};

  const freeExtrasSelection = booking.freeExtras || {};

  const paidInsuranceSelection = booking.paidInsurance || {};

  const freeInsuranceSelection = booking.freeInsurance || {};

  // ==========================================================
  // ACTIVE PAID EXTRAS
  // ==========================================================

  const activePaidExtras = paidExtras
    .filter((item) => {
      const id = Number(item.optdet_id ?? item.optitem_id);

      return isSelectedId(paidExtrasSelection, id);
    })
    .map((item) => {
      const id = Number(item.optdet_id ?? item.optitem_id);

      let quantity = 1;

      if (
        paidExtrasSelection &&
        typeof paidExtrasSelection === "object" &&
        !Array.isArray(paidExtrasSelection)
      ) {
        quantity = Number(paidExtrasSelection[id] || 1);
      }

      const dayRate = Number(
        item.dayRate ??
          item.day_rate ??
          item.ratePerDay ??
          item.rate_per_day ??
          0
      );

      let baseTotal = dayRate * rentalDays;

      const maxRate = Number(item.maxRate || 0);

      if (maxRate > 0 && baseTotal > maxRate) {
        baseTotal = maxRate;
      }

      return {
        ...item,

        id,

        quantity,

        dayRate,

        total: baseTotal * quantity,
      };
    });

  // ==========================================================
  // ACTIVE FREE EXTRAS
  // ==========================================================

  const activeFreeExtras = freeExtras.filter((item) => {
    const id = Number(item.optdet_id ?? item.optitem_id);

    return isSelectedId(freeExtrasSelection, id);
  });

  // ==========================================================
  // ACTIVE PAID INSURANCE
  // ==========================================================

  const activePaidInsurance = paidInsurance
    .filter((item) => {
      const id = Number(item.insdtl_id ?? item.insitem_id);

      return isSelectedId(paidInsuranceSelection, id);
    })
    .map((item) => {
      const id = Number(item.insdtl_id ?? item.insitem_id);

      const ratePerDay = Number(
        item.chargePerDay ||
          item.ratePerDay ||
          item.rate_per_day ||
          item.online_itemrate ||
          0
      );

      return {
        ...item,

        id,

        ratePerDay,

        total: ratePerDay * rentalDays,
      };
    });

  // ==========================================================
  // ACTIVE FREE INSURANCE
  // ==========================================================

  const activeFreeInsurance = freeInsurance.filter((item) => {
    const id = Number(item.insitem_id ?? item.insdtl_id);

    return isSelectedId(freeInsuranceSelection, id);
  });

  // ==========================================================
  // CALCULATED TOTALS
  // ==========================================================

  const calculatedPaidExtrasTotal = activePaidExtras.reduce(
    (sum, item) => sum + Number(item.total || 0),
    0
  );

  const calculatedPaidInsuranceTotal = activePaidInsurance.reduce(
    (sum, item) => sum + Number(item.total || 0),
    0
  );

  /*
   * Prefer database final total when it exists.
   * Otherwise calculate from the displayed components,
   * minus the coupon discount.
   */

  const calculatedFinalTotal = Math.max(
    0,
    rentalTotal +
      calculatedPaidExtrasTotal +
      calculatedPaidInsuranceTotal -
      couponAmt
  );

  const displayFinalTotal = finalTotal > 0 ? finalTotal : calculatedFinalTotal;

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div className="p20">
      <div className="container container2 p0">
        <div
          className="col-md-12 main-content"
          style={{
            float: "left",
          }}
        >
          {/* ==================================================
              THANK YOU
          ================================================== */}

          <div className="booking-card">
            <div className="card-header">
              <h3 style={{ color: "#119b6e" }}>
                {ot("others5", "Thank you for your reservation")}
              </h3>

              <p
                style={{
                  marginTop: "6px",
                }}
              >
                {ot("others17", "Your booking reference number Is")}

                {" : "}

                <strong
                  style={{
                    color: "#ffffff",
                    background: "#119b6e",
                    padding: "2px 12px",
                    borderRadius: "13px",
                  }}
                >
                  {fullBookingNo}
                </strong>
              </p>
            </div>

            <div
              className="conform1"
              style={{
                width: "80%",
                float: "left",
              }}
            >
              <p>
                {ot("others10", "Dear")} {customer.title} {customer.firstName}{" "}
                {customer.lastName},
              </p>

              <p>
                {ot(
                  "others11",
                  "Thank you for your booking. Your reservation has now been placed. Show this voucher in print or save it on your mobile device at receipt of the car. Your booking reference number is :"
                )}{" "}
                <strong style={{ color: "#119b6e" }}>{fullBookingNo}</strong>
              </p>

              <p>
                {ot(
                  "others12",
                  "Please keep this number for future reference. This reservation includes all taxes, unlimited mileage and a map of Crete."
                )}
              </p>
            </div>

            {/* PRINT */}

            <div
              style={{
                width: "100px",
                float: "right",
                textAlign: "center",
              }}
            >
              <div
                onClick={() => window.print()}
                style={{
                  cursor: "pointer",
                }}
              >
                <img
                  src={printImage}
                  alt={ot("others7", "Print preview")}
                  style={{
                    display: "block",
                    margin: "0 auto",
                    width: "50px",
                    height: "50px",
                  }}
                />
              </div>

              <div className="tbltxtnew">{ot("others7", "Print preview")}</div>
            </div>

            {/* NEW RESERVATION */}

            <div
              style={{
                width: "100px",
                float: "right",
                textAlign: "center",
              }}
            >
              <div
                onClick={() => navigate("/bookacar")}
                style={{
                  cursor: "pointer",
                }}
              >
                <img
                  src={newReservationImage}
                  alt={ot("others6", "New reservation")}
                  style={{
                    display: "block",
                    margin: "0 auto",
                    width: "50px",
                    height: "50px",
                  }}
                />
              </div>

              <div
                className="tbltxtnew"
                onClick={() => navigate("/bookacar")}
                style={{
                  cursor: "pointer",
                }}
              >
                {ot("others6", "New reservation")}
              </div>
            </div>

            <div className="clearfix" />
          </div>

          <div className="row">
            {/* ==========================================
                  LEFT COLUMN - BOOKING DETAILS (col-md-7)
              ========================================== */}

            <div className="col-md-7">
              <div className="l-box">
                <div className="extra-details">
                  {t("othersnewstatics.othersnewstatic59", "Your itinerary")}{" "}
                  {t("ratechart.ratechart2", "Total")}{" "}
                  {t("extracharges.extracharges10", "Day")}{" "}
                  {t("totalDaysOfHire", "of hire")} {rentalDays}{" "}
                  {t("bookingengine.bookingengine21", "day(s)")}
                </div>

                <div className="car-box price-box">
                  <table className="table word-break" width="100%">
                    <tbody>
                      <tr>
                        <td>
                          <strong>
                            {bt("bookingengine0", "Pick up location")}
                          </strong>
                          {" : "}
                          {getLocationName(criteria.cmbPickLocation)}
                        </td>
                      </tr>

                      <tr>
                        <td>
                          <strong>
                            {bt("bookingengine1", "Pick up date")}
                          </strong>
                          {" : "}
                          {criteria.pickdate} :{" "}
                          {getTime(criteria.cmbpickHour, criteria.cmbpickmin)}
                        </td>
                      </tr>

                      {/* <tr>
                      <td>
                        <strong>{bt("bookingengine2", "Pick up time")}</strong>
                        {" : "}
                        {getTime(criteria.cmbpickHour, criteria.cmbpickmin)}
                      </td>
                    </tr> */}

                      <tr>
                        <td>
                          <strong>
                            {bt("bookingengine3", "Return location")}
                          </strong>
                          {" : "}
                          {getLocationName(criteria.cmbDropLocation)}
                        </td>
                      </tr>

                      <tr>
                        <td>
                          <strong>{bt("bookingengine4", "Return date")}</strong>
                          {" : "}
                          {criteria.returndate} :{" "}
                          {getTime(
                            criteria.cmbreturnHour,
                            criteria.cmbreturnmin
                          )}
                        </td>
                      </tr>

                      {/* <tr>
            <td>
              <strong>{bt("bookingengine5", "Return time")}</strong>
              {" : "}
              {getTime(criteria.cmbreturnHour, criteria.cmbreturnmin)}
            </td>
          </tr> */}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="rental-card">
                <div className="card-main-content">
                  <div className="row">
                    <div className="col-md-9">
                      <h2 className="car-title">
                        {carName} <span>({bt("orSimilar", "or similar")})</span>
                      </h2>

                      <div className="car-subtitle">{category}</div>
                    </div>

                    <div className="col-md-3">
                      <div className="price-container">
                        <div className="price-label">
                          {t("ratechart.ratechart21", "Final Price")}
                        </div>

                        <div className="main-price">
                          € {displayFinalTotal.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    {/* CAR IMAGE */}
                    <div className="col-md-5">
                      <div className="image-container">
                        {carImage && (
                          <img
                            src={carImage}
                            alt={carName}
                            className="car-img"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        )}
                      </div>
                    </div>

                    {/* FEATURES */}
                    <div className="col-md-7">
                      <div className="features-container">
                        <div className="specs-row">
                          <span className="spec-tag">👤 {passengers}</span>
                          <span className="spec-tag">🧳 {luggage}</span>

                          <span className="spec-tag">
                            ❄️{" "}
                            {hasAirCo
                              ? t("carparams.carparams7", "Yes")
                              : t("carparams.carparams8", "No")}
                          </span>

                          <span className="spec-tag">🚪 {doors}</span>
                        </div>

                        {mainFeatures.map((feature, index) => (
                          <div
                            className="feature-item"
                            key={`main-${index}`}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              marginBottom: "8px",
                            }}
                          >
                            <img
                              src={tickImage}
                              alt={bt("included", "Included")}
                              style={{
                                width: "20px",
                                height: "20px",
                                marginRight: "8px",
                              }}
                            />

                            <strong>{feature}</strong>
                          </div>
                        ))}

                        {extraFeatures.length > 0 && (
                          <div style={{ marginTop: "10px" }}>
                            <button
                              type="button"
                              onClick={() =>
                                setFeaturesExpanded((prev) => !prev)
                              }
                              style={{
                                border: "none",
                                background: "transparent",
                                color: "#087cff",
                                fontWeight: "600",
                                cursor: "pointer",
                              }}
                            >
                              {featuresExpanded
                                ? t(
                                    "othersnewstatics.othersnewstatic51",
                                    "Show less"
                                  )
                                : (() => {
                                    const [first, ...rest] = t(
                                      "othersnewstatics.othersnewstatic46",
                                      "Show More"
                                    ).split(" ");

                                    return `${first} ${
                                      extraFeatures.length
                                    } ${rest.join(" ")}`;
                                  })()}
                            </button>

                            {featuresExpanded && (
                              <div style={{ marginTop: "10px" }}>
                                {extraFeatures.map((feature, index) => (
                                  <div
                                    key={`extra-${index}`}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      marginBottom: "8px",
                                    }}
                                  >
                                    <img
                                      src={tickImage}
                                      alt={bt("included", "Included")}
                                      style={{
                                        width: "20px",
                                        height: "20px",
                                        marginRight: "8px",
                                      }}
                                    />

                                    <strong>{feature}</strong>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* NOTE */}
                  <div className="actions-container">
                    <div className="car-d notice">
                      {t("ratechart.ratechart19", "Note:")}{" "}
                      {t(
                        "carparams.carparams21",
                        "The minimum driver age for this car is"
                      )}{" "}
                      21
                    </div>
                  </div>
                </div>
              </div>

              {/* ==================================================
              RATE CHART
          ================================================== */}

              <div className="rate">{rt("ratechart4", "Rate charts")}</div>

              <div className="price-box">
                <table
                  className="table word-break"
                  width="100%"
                  style={{
                    marginBottom: 0,
                  }}
                >
                  <thead>
                    <tr>
                      <th
                        className="optionalextra"
                        style={{
                          width: "35%",
                        }}
                      >
                        {rt("quote", "Quote")}
                      </th>

                      <th
                        className="optionalextra"
                        style={{
                          width: "35%",
                          textAlign: "center",
                        }}
                      >
                        {rt("ratechart1", "Calculation")}
                      </th>

                      <th
                        className="optionalextra"
                        style={{
                          width: "30%",
                          textAlign: "right",
                        }}
                      >
                        {rt("ratechart2", "Total")}
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {/* RENTAL PRICE */}

                    <tr>
                      <td>
                        <strong>
                          {rt("ratechart10", "Total Rental price")}
                        </strong>
                      </td>

                      <td
                        style={{
                          textAlign: "center",
                          color: "#0a9311",
                          fontWeight: "600",
                        }}
                      >
                        {rentalDays}

                        {" × €"}

                        {(rentalDays > 0
                          ? perdaycarprice
                          : rentalPrice
                        ).toFixed(2)}
                      </td>

                      <td
                        style={{
                          textAlign: "right",
                          color: "#0a9311",
                          fontWeight: "600",
                        }}
                      >
                        € {rentalPrice.toFixed(2)}
                      </td>
                    </tr>

                    {/* PICKUP */}

                    {pickupCharge > 0 && (
                      <tr>
                        <td>{rt("ratechart6", "Pick up charge")}</td>

                        <td />

                        <td
                          style={{
                            textAlign: "right",
                          }}
                        >
                          € {pickupCharge.toFixed(2)}
                        </td>
                      </tr>
                    )}

                    {/* DROPOFF */}

                    {dropoffCharge > 0 && (
                      <tr>
                        <td>{rt("ratechart3", "Drop off charge")}</td>

                        <td />

                        <td
                          style={{
                            textAlign: "right",
                          }}
                        >
                          € {dropoffCharge.toFixed(2)}
                        </td>
                      </tr>
                    )}

                    {/* ONE WAY */}

                    {oneWayCharge > 0 && (
                      <tr>
                        <td>{rt("ratechart15", "One way rental charges")}</td>

                        <td />

                        <td
                          style={{
                            textAlign: "right",
                          }}
                        >
                          € {oneWayCharge.toFixed(2)}
                        </td>
                      </tr>
                    )}

                    {/* RENTAL TOTAL */}
                    {/* 
                <tr>
                  <td>
                    <strong>
                      {rt("ratechart10", "Total rental price")}
                    </strong>
                  </td>

                  <td />

                  <td
                    style={{
                      textAlign: "right",
                      color: "#8a74dd",
                      fontWeight: "600",
                    }}
                  >
                    € {rentalTotal.toFixed(2)}
                  </td>
                </tr> */}

                    {/* OPTIONAL EXTRAS HEADING */}

                    {activePaidExtras.length > 0 && (
                      <tr>
                        <td
                          colSpan="3"
                          style={{
                            fontWeight: "600",
                            paddingTop: "12px",
                          }}
                        >
                          {et("extracharges2", "Optional extras")}
                        </td>
                      </tr>
                    )}

                    {/* OPTIONAL EXTRAS */}

                    {activePaidExtras.map((item) => {
                      const itemName =
                        item.name ||
                        item.optitem_name ||
                        item.optdet_name ||
                        et("optionalExtra", "Optional extra");

                      const dayRate = Number(item.dayRate || 0);

                      const quantity = Number(item.quantity || 1);

                      const itemTotal = Number(item.total || 0);

                      return (
                        <tr key={`rate-extra-${item.id}`}>
                          <td>{itemName}</td>

                          <td
                            style={{
                              textAlign: "center",
                              color: "#8a74dd",
                              fontWeight: "600",
                            }}
                          >
                            €{dayRate.toFixed(2)}
                            {" × "}
                            {rentalDays}
                            {" × "}
                            {quantity}
                          </td>

                          <td
                            style={{
                              textAlign: "right",
                              fontWeight: "600",
                              color: "#8a74dd",
                            }}
                          >
                            € {itemTotal.toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}

                    {/* OPTIONAL EXTRAS TOTAL */}

                    {activePaidExtras.length > 0 && (
                      <tr>
                        <td>
                          <strong>
                            {et("totalExtraCharges", "Total extra charges")}
                          </strong>
                        </td>

                        <td />

                        <td
                          style={{
                            textAlign: "right",
                            color: "#ff6600",
                            fontWeight: "600",
                          }}
                        >
                          € {calculatedPaidExtrasTotal.toFixed(2)}
                        </td>
                      </tr>
                    )}

                    {/* PAID INSURANCE */}

                    {activePaidInsurance.length > 0 && (
                      <tr>
                        <td>
                          <strong>
                            {et("paidInsurance", "Paid insurance")}
                          </strong>
                        </td>

                        <td />

                        <td
                          style={{
                            textAlign: "right",
                          }}
                        >
                          € {calculatedPaidInsuranceTotal.toFixed(2)}
                        </td>
                      </tr>
                    )}

                    {/* COUPON DISCOUNT */}

                    {couponAmt > 0 && (
                      <tr>
                        <td
                          style={{
                            color: "#0a9311",
                            fontWeight: "600",
                          }}
                        >
                          {t("others.others23", "Coupon Code")}{" "}
                          {/* {t("others.others28", "Discount")} */}
                          {/* {couponCode && (
                        <span style={{ fontWeight: "400" }}>
                          {" "}
                          ({couponCode})
                        </span>
                      )} */}
                          {couponPerc > 0 && (
                            <span
                              style={{
                                fontSize: "13px",
                                fontWeight: "bolder",
                                marginLeft: "1px",
                              }}
                            >
                              ({couponPerc}%)
                            </span>
                          )}
                          &nbsp;(Of {rt("ratechart10", "Total rental price")})
                        </td>

                        <td />

                        <td
                          style={{
                            textAlign: "right",
                            color: "#0a9311",
                            fontWeight: "600",
                          }}
                        >
                          - € {couponAmt.toFixed(2)}
                        </td>
                      </tr>
                    )}

                    {/* FINAL TOTAL */}

                    {/* FINAL TOTAL: old price (struck) + discounted price */}

                    <tr>
                      <td colSpan="2" style={{ background: "#edf7ec" }}>
                        <strong>
                          {rt("ratechart14", "Final total rental price")}
                        </strong>
                      </td>

                      <td
                        style={{
                          background: "#edf7ec",
                          textAlign: "right",
                          fontSize: "18px",
                          fontWeight: "700",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {couponAmt > 0 && (
                          <span
                            style={{
                              textDecoration: "line-through",
                              color: "red",
                              fontWeight: "bolder",
                              fontSize: "15px",
                              marginRight: "12px",
                            }}
                          >
                            € {(displayFinalTotal + couponAmt).toFixed(2)}
                          </span>
                        )}
                        € {displayFinalTotal.toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="col-md-5">
              {/* ==================================================
              EXTRA DETAILS
          ================================================== */}

              {activePaidExtras.length > 0 && (
                <>
                  <div className="extra-details">
                    {t("othersnewstatics.othersnewstatic52", "Extra details")}
                    {" : "}
                  </div>

                  <div
                    className="extras car-box price-box"
                    style={{
                      padding: 0,
                      marginBottom: "15px",
                    }}
                  >
                    <table
                      className="table"
                      width="100%"
                      style={{
                        marginBottom: 0,
                      }}
                    >
                      <thead>
                        <tr>
                          <th className="optionalextra">
                            {t("extracharges.extracharges4", "Items")}
                          </th>

                          <th className="optionalextra">
                            {t("extracharges.extracharges5", "Rate per day")}
                          </th>

                          <th className="optionalextra">
                            {t("extracharges.extracharges2", "Selection")}
                          </th>

                          <th className="optionalextra">
                            {t("extracharges.extracharges6", "Total")}
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {activePaidExtras.map((item) => {
                          const itemName =
                            item.name ||
                            item.optitem_name ||
                            item.optdet_name ||
                            et("optionalExtra", "Optional extra");

                          const dayRate = Number(item.dayRate || 0);

                          const quantity = Number(item.quantity || 1);

                          const itemTotal = Number(item.total || 0);

                          return (
                            <tr key={`extra-detail-${item.id}`}>
                              <td>{itemName}</td>

                              <td>
                                €{dayRate.toFixed(2)}
                                {" / "}
                                {et("day", "Day")}
                              </td>

                              <td>{quantity}</td>

                              <td>
                                <strong>€{itemTotal.toFixed(2)}</strong>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {/* ==================================================
              FREE EXTRAS
          ================================================== */}

              {activeFreeExtras.length > 0 && (
                <>
                  <div className="extra-details">
                    {et("extracharges1", "Free optional extras")}
                  </div>

                  <div className="extras car-box price-box">
                    <table className="table" width="100%">
                      <tbody>
                        {activeFreeExtras.map((item) => (
                          <tr key={item.optdet_id ?? item.optitem_id}>
                            <td>
                              {item.name ||
                                item.optitem_name ||
                                item.optdet_name ||
                                ""}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              {/* ==================================================
              INSURANCE
          ================================================== */}

              <div className="extra-details">
                {et("extracharges7", "Insurances")}
              </div>

              {/* PAID INSURANCE */}

              {activePaidInsurance.length > 0 && (
                <div className="extras car-box price-box">
                  <table className="table" width="100%">
                    <thead>
                      <tr>
                        <th>{et("item", "Item")}</th>

                        <th>{et("ratePerDay", "Rate per day")}</th>

                        <th>{rt("total", "Total")}</th>
                      </tr>
                    </thead>

                    <tbody>
                      {activePaidInsurance.map((item) => (
                        <tr key={item.id}>
                          <td>
                            {item.name ||
                              item.insitem_name ||
                              item.insdtl_name ||
                              ""}
                          </td>

                          <td>
                            €{Number(item.ratePerDay || 0).toFixed(2)}
                            {" / "}
                            {bt("day", "day")}
                          </td>

                          <td>
                            <strong>
                              €{Number(item.total || 0).toFixed(2)}
                            </strong>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* FREE INSURANCE */}

              {activeFreeInsurance.length > 0 && (
                <div className="extras car-box price-box">
                  <table className="table" width="100%">
                    <tbody>
                      {activeFreeInsurance.map((item) => (
                        <tr key={item.insitem_id ?? item.insdtl_id}>
                          <td>
                            {item.name ||
                              item.insitem_name ||
                              item.insdtl_name ||
                              ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ==================================================
              PERSONAL DETAILS
          ================================================== */}

              <div className="extra-details">
                {pt("personaldtls0", "Personal details")}
              </div>

              <div className="extras car-box price-box">
                <table className="table" width="100%">
                  <tbody>
                    <tr>
                      <td>
                        <strong>{pt("personaldtls2", "First name")}</strong>
                        <br />
                        {customer.title} {customer.firstName}
                      </td>
                      <td>
                        <strong>{pt("personaldtls3", "Family name")}</strong>
                        <br />
                        {customer.lastName}
                      </td>
                    </tr>

                    <tr>
                      <td>
                        <strong>{pt("personaldtls8", "Mobile no")}</strong>
                        <br />
                        {customer.mobile || customer.phone}
                      </td>
                      <td>
                        <strong>{pt("personaldtls4", "Email ID")}</strong>
                        <br />
                        {customer.email}
                      </td>
                    </tr>

                    <tr>
                      <td>
                        <strong>{pt("personaldtls14", "Home Address")}</strong>
                        <br />
                        {customer.address}
                      </td>
                      <td>
                        <strong>{pt("personaldtls16", "Zip code")}</strong>
                        <br />
                        {customer.postcode}
                      </td>
                    </tr>

                    <tr>
                      <td>
                        <strong>{pt("personaldtls15", "City")}</strong>
                        <br />
                        {customer.city}
                      </td>
                      <td>
                        <strong>{pt("personaldtls10", "Country")}</strong>
                        <br />
                        {customer.country}
                      </td>
                    </tr>

                    <tr>
                      <td>
                        <strong>
                          {pt("personaldtls9", "Pick up location")}
                        </strong>
                        <br />
                        {getLocationName(criteria.cmbPickLocation)}
                      </td>
                      <td>
                        <strong>
                          {pt("personaldtls17", "Drop off location")}
                        </strong>
                        <br />
                        {getLocationName(criteria.cmbDropLocation)}
                      </td>
                    </tr>

                    <tr>
                      <td>
                        <strong>
                          {pt("personaldtls23", "Pick up comments")}
                        </strong>
                        <br />
                        {customer.PickDetails}
                      </td>
                      <td>
                        <strong>
                          {pt("personaldtls24", "Drop off comments")}
                        </strong>
                        <br />
                        {customer.DropDetails}
                      </td>
                    </tr>

                    <tr>
                      <td>
                        <strong>{t("menu.menu19", "Regular client")}</strong>
                        <br />
                        {customer.regualclientid}
                      </td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConfirmBooking;
